import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BaseScreenComponent } from '@presentationShared';
import { MenuItem } from '@presentationTypes';
import { menuItems, menuItemsMobile } from '@presentationUtils';
import { ButtonColor, ButtonComponent, SharedModule, UrlHelperService } from '@shared';
import gsap from 'gsap';

@Component({
  selector: 'k-navbar',
  imports: [ ButtonComponent, SharedModule, RouterModule ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent extends BaseScreenComponent implements AfterViewInit, OnDestroy {
  private translate: TranslateService = inject(TranslateService);
  private renderer: Renderer2 = inject(Renderer2);
  private router: Router = inject(Router);
  private urlHelper: UrlHelperService = inject(UrlHelperService);
  private document: Document = inject(DOCUMENT);
  private platformId: object = inject(PLATFORM_ID);

  availableLanguages: readonly string[] = this.translate.getLangs();
  buttonColors = ButtonColor;

  overlay: Signal<ElementRef | undefined> = viewChild('overlay', { read: ElementRef });
  mobileMenu: Signal<ElementRef | undefined> = viewChild('mobileMenu', { read: ElementRef });
  menuItems: Signal<MenuItem[]> = signal(menuItems);
  menuItemsMobile: Signal<MenuItem[]> = signal(menuItemsMobile);
  menuOpened: WritableSignal<boolean> = signal(false);
  langChange: Signal<LangChangeEvent | undefined> = toSignal(this.translate.onLangChange);
  languageButtonLabelSig: Signal<string> = computed(() =>
    this.langChange() ? (this.availableLanguages.find((i) => i !== this.translate.currentLang) ?? 'en') : 'en',
  );

  private routeSignalsCache = new Map<string, Signal<boolean>>();
  private footerVisible: WritableSignal<boolean> = signal(false);
  private scrollUnlisten: (() => void) | null = null;

  menuItemsFiltered = computed(() => {
    return this.menuItems().filter((item: MenuItem) => !item?.onlyFooter);
  });

  mobileMenuItemsFiltered = computed(() => {
    return this.menuItemsMobile().filter((item: MenuItem) => !item?.onlyFooter);
  });

  activeMenuItemId = computed(() => {
    if (this.footerVisible()) return 'contact';
    const activeItem = menuItems.find(
      item => item.routerLink && this.routeSignalsCache.get(item.routerLink)?.(),
    );
    return activeItem?.id ?? null;
  });

  constructor() {
    super();
    [ ...menuItems, ...menuItemsMobile ].forEach(item => {
      if (item.routerLink && typeof item.routerLink === 'string') {
        const signal = toSignal(this.urlHelper.isRouteActive(item.routerLink), { initialValue: false });
        this.routeSignalsCache.set(item.routerLink, signal);
      }
    });
  }

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  ngAfterViewInit() {
    if (!this.isBrowser) {
      return;
    }

    gsap.set(this.overlay()?.nativeElement, { opacity: 0, pointerEvents: 'none' });
    gsap.set(this.mobileMenu()?.nativeElement, { y: '100%' });
    this.setupScrollDetection();
  }

  ngOnDestroy(): void {
    this.scrollUnlisten?.();
  }

  private setupScrollDetection(): void {
    this.scrollUnlisten = this.renderer.listen('window', 'scroll', () => {
      const scrollBottom = window.scrollY + window.innerHeight;
      const pageHeight = this.document.documentElement.scrollHeight;
      this.footerVisible.set(scrollBottom >= pageHeight - 50);
    });
  }

  changeLanguage(): void {
    this.translate.use(this.languageButtonLabelSig());
  }

  openMenu(): void {
    if (!this.isBrowser) {
      return;
    }

    this.menuOpened.update(() => true);
    this.renderer.addClass(this.document.body, 'no-scroll');
    this.gtagService.trackEvent('Button', 'Click', 'open-mobile-menu');
    this.openAnimation();
  }

  closeMenu(): void {
    if (!this.isBrowser) {
      return;
    }

    this.renderer.removeClass(this.document.body, 'no-scroll');
    this.closeAnimation();
  }

  clickMenuElement(menuItem: MenuItem, label: string): void {
    this.gtagService.trackEvent('Button', 'Click', label + menuItem.id);
    this.closeMenu();
    this.routerOrNavigate(menuItem);
  }

  navigateOrScroll(menuItem: MenuItem, label?: string): void {
    if (label) {
      this.gtagService.trackEvent('Button', 'Click', label + menuItem.id);
    }
    this.routerOrNavigate(menuItem);
  }

  private routerOrNavigate(menuItem: MenuItem): void {
    if (menuItem?.scrollToBottom) {
      this.scrollToBottom();
      return;
    }

    if (menuItem?.routerLink) {
      if (menuItem.routerLink === '/home') {
        this.navigateToHomeTop();
        return;
      }
      this.router.navigate([ menuItem.routerLink as string ]);
      return;
    }

    if (menuItem.link) {
      if (this.isBrowser) {
        window.open(menuItem.link, '_blank');
      }
    }
  }

  navigateToHomeTop(label?: string): void {
    if (label) {
      this.gtagService.trackEvent('Button', 'Click', label + 'home_top');
    }

    if (this.router.url.startsWith('/home')) {
      this.scrollToTop();
      return;
    }

    this.router.navigate([ '/home' ]);
  }

  private openAnimation(): void {
    gsap.set(this.overlay()?.nativeElement, { pointerEvents: 'auto' });

    gsap.to(this.overlay()?.nativeElement, {
      opacity: 1,
      duration: 0.3,
      ease: 'power3.out',
    });

    gsap.to(this.mobileMenu()?.nativeElement, {
      y: 0,
      duration: 0.3,
      ease: 'power3.out',
    });
  }

  private closeAnimation(): void {
    gsap.to(this.overlay()?.nativeElement, {
      opacity: 0,
      duration: 0.25,
      ease: 'power3.in',
      onComplete: () => {
        gsap.set(this.overlay()?.nativeElement, { pointerEvents: 'none' });
      },
    });

    gsap.to(this.mobileMenu()?.nativeElement, {
      y: '100%',
      duration: 0.25,
      ease: 'power3.in',
      onComplete: () => {
        this.menuOpened.update(() => false);
      },
    });
  }
}
